// Type definitions for Small Group Lesson Plan functionality
// Based on the implementation plan Phase 1

export interface LessonPlanForm {
  subject?: 'ELA' | 'Math'; // NEW: Optional for backward compatibility
  standardId: string;
  // Subject-agnostic fields
  difficulty?: string; // NEW: Replaces lexileLevel for generic use
  contentType?: string; // NEW: Replaces genre for generic use
  contentLength?: number; // NEW: Replaces paragraphCount for generic use
  // Legacy ELA fields (kept for backward compatibility)
  lexileLevel?: string;
  genre?: string;
  paragraphCount?: number;
  // Common fields
  topic: string;
  independentPracticeTopic: string; // New field for independent practice section
  language: string; // Main language for story and activities
  revisingAndEditing: boolean; // New field for STAAR-style revising and editing passages
}

export interface StoryMetadata {
  id: string;
  title: string;
  wordCount: number;
  readingTimeMinutes: number;
  createdAt: Date;
}

export interface Story {
  id: string;
  title: string;
  content: string;
  metadata: StoryMetadata;
}

// Translation status tracking
export type TranslationStatus = 'valid' | 'outdated' | 'missing' | 'error';

export interface TranslationMetadata {
  lastUpdated: Date;
  sourceContentHash: string; // Hash of the source content when translated
  status: TranslationStatus;
  language: string;
}

export interface LessonSection {
  id: string;
  type: 'introduction' | 'independent';
  story: Story | null;
  activities: string;
  // Optional bilingual fields when language selection is "both"
  storySpanish?: Story | null;
  activitiesSpanish?: string;
  // Translation metadata for tracking status
  translationMetadata?: {
    storySpanish?: TranslationMetadata;
    activitiesSpanish?: TranslationMetadata;
  };
  duration?: number;
  isGenerated: boolean;
  isStreaming: boolean;
}

export interface StreamingState {
  isActive: boolean;
  progress: number;
  wordsPerMinute: number;
  estimatedCompletion: number;
  currentContent: string;
  error?: string;
}

export interface LessonPlan {
  id: string | null;
  form: LessonPlanForm;
  sections: {
    introduction: LessonSection;
    independent: LessonSection;
  };
  metadata: {
    createdAt: Date | null;
    updatedAt: Date | null;
    version: number;
  };
}

export interface FormErrors {
  standard?: string;
  lexile?: string;
  genre?: string;
  topic?: string;
  paragraphs?: string;
}

// Component prop interfaces
export interface SectionTabsProps {
  activeTab: number;
  onTabChange: (tab: number) => void;
  sections: {
    introduction: LessonSection;
    independent: LessonSection;
  };
  disabled?: boolean;
}

export interface IntroductionSectionProps {
  section: LessonSection;
  isStreaming: {
    story: boolean;
    activities: boolean;
  };
  onRegenerate: (type: 'story' | 'activities') => void;
  onEdit: (content: string, type: 'story' | 'activities') => void;
  genre?: string; // Add genre for format-specific rendering
  subject?: string; // NEW: Subject for dynamic labels
}

export interface StoryDisplayProps {
  story: Story | null;
  isStreaming: boolean;
  editable?: boolean;
  onEdit?: (content: string) => void;
  genre?: string; // Add genre for format-specific rendering
}

export interface ActivityDisplayProps {
  content: string;
  isStreaming: boolean;
  type: 'introduction' | 'practice';
  onEdit?: (content: string) => void;
}

// Math difficulty level configuration
export interface MathDifficulty {
  grades: string[];
  levels: Array<{ value: string; label: string }>;
}

export const MATH_DIFFICULTY_LEVELS: MathDifficulty[] = [
  {
    grades: ['K', '1'],
    levels: [
      { value: 'concrete', label: 'Concrete (with objects/manipulatives)' },
      { value: 'pictorial', label: 'Pictorial (with drawings)' },
      { value: 'abstract', label: 'Abstract (numbers only)' },
      { value: 'mixed', label: 'Mixed Representations' }
    ]
  },
  {
    grades: ['2', '3', '4', '5'],
    levels: [
      { value: 'procedural', label: 'Procedural (step-by-step)' },
      { value: 'conceptual', label: 'Conceptual Understanding' },
      { value: 'mixed', label: 'Mixed (procedural + conceptual)' },
      { value: 'challenge', label: 'Challenge/Mastery' }
    ]
  },
  {
    grades: ['6', '7', '8'],
    levels: [
      { value: 'foundation', label: 'Foundation Level' },
      { value: 'mastery', label: 'Mastery Level' },
      { value: 'application', label: 'Application Level' },
      { value: 'advanced', label: 'Advanced Reasoning' }
    ]
  }
];

// Initial state constants
export const initialFormState: LessonPlanForm = {
  subject: 'ELA', // NEW: default
  standardId: '',
  difficulty: '', // NEW: subject-agnostic field
  contentType: 'Fiction', // NEW: subject-agnostic field
  contentLength: 3, // NEW: subject-agnostic field
  // Legacy ELA fields for backward compatibility
  lexileLevel: '',
  genre: 'Fiction',
  paragraphCount: 3,
  topic: '',
  independentPracticeTopic: '',
  language: 'english',
  revisingAndEditing: false,
};

export const initialSectionState: LessonSection = {
  id: '',
  type: 'introduction',
  story: null,
  activities: '',
  duration: 20,
  isGenerated: false,
  isStreaming: false,
};

export const initialLessonPlan: LessonPlan = {
  id: null,
  form: initialFormState,
  sections: {
    introduction: { ...initialSectionState, type: 'introduction' },
    independent: { ...initialSectionState, type: 'independent' },
  },
  metadata: {
    createdAt: null,
    updatedAt: null,
    version: 1,
  },
}; 